import {
  Bell,
  CalendarCheck,
  CalendarX,
  CalendarPlus,
  LogIn,
  LogOut,
  AlertTriangle,
  Award,
  AlertOctagon,
  PartyPopper,
  Megaphone,
  Info,
} from 'lucide-react';
import type { NotificationType } from '../api/notificationsApi';

/**
 * Maps each `NotificationType` to a Lucide icon + Tailwind tone classes so
 * the bell dropdown, Notification Center, and toasts share one visual
 * language for the eleven event kinds.
 */
export const NOTIFICATION_ICONS: Record<
  NotificationType,
  { icon: typeof Bell; tone: string; bg: string }
> = {
  leave_request_submitted: {
    icon: CalendarPlus,
    tone: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
  },
  leave_approved: {
    icon: CalendarCheck,
    tone: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
  },
  leave_rejected: {
    icon: CalendarX,
    tone: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-100 dark:bg-rose-900/30',
  },
  attendance_check_in: {
    icon: LogIn,
    tone: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
  },
  attendance_check_out: {
    icon: LogOut,
    tone: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-100 dark:bg-sky-900/30',
  },
  attendance_late: {
    icon: AlertTriangle,
    tone: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-100 dark:bg-amber-900/30',
  },
  reward_granted: {
    icon: Award,
    tone: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-100 dark:bg-amber-900/30',
  },
  penalty_applied: {
    icon: AlertOctagon,
    tone: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-100 dark:bg-rose-900/30',
  },
  holiday_announced: {
    icon: PartyPopper,
    tone: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-100 dark:bg-violet-900/30',
  },
  broadcast: {
    icon: Megaphone,
    tone: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
  },
  system: {
    icon: Info,
    tone: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-100 dark:bg-slate-800',
  },
};

/** Look up the icon component for a given notification type with a safe fallback. */
export function getNotificationIcon(type: NotificationType) {
  return NOTIFICATION_ICONS[type] ?? NOTIFICATION_ICONS.system;
}

/**
 * Locale-aware relative time formatter ("5 دقائق", "2h ago"). Kept tiny so we
 * don't pull `dayjs/relativeTime` plugin loaders just for the bell.
 */
export function formatRelativeTime(
  isoDate: string,
  isRtl: boolean,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diff = Math.max(0, now - then);
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);

  if (sec < 60) return t('notifications.time.now', { defaultValue: isRtl ? 'الآن' : 'just now' });
  if (min < 60)
    return t('notifications.time.minutes', {
      count: min,
      defaultValue: isRtl ? `قبل ${min} دقيقة` : `${min}m ago`,
    });
  if (hr < 24)
    return t('notifications.time.hours', {
      count: hr,
      defaultValue: isRtl ? `قبل ${hr} ساعة` : `${hr}h ago`,
    });
  return t('notifications.time.days', {
    count: day,
    defaultValue: isRtl ? `قبل ${day} يوم` : `${day}d ago`,
  });
}
